import type { Rarity, TraitSelection } from "@/types";
import {
  ACCESSORY_BACK,
  ACCESSORY_FRONT,
  BACKGROUND_ART,
  BACKGROUND_DEFS,
  EFFECTS,
  EYE_COLORS,
  EYES,
  FACE,
  HAIR_BACK,
  HAIR_FRONT,
  HAIR_PALETTES,
  OUTFIT_BACK,
  OUTFIT_FRONT,
  OUTFIT_PALETTES,
  RIG_CATALOG_HASH,
  RIG_TRAIT_OPTIONS,
  RIG_VERSION,
  SKIN_PALETTES,
} from "@/generated/rig-catalog";

export { RIG_CATALOG_HASH, RIG_VERSION };

const assertTraits = (traits: TraitSelection) => {
  const counts = {
    base: RIG_TRAIT_OPTIONS.base.length,
    eyes: RIG_TRAIT_OPTIONS.eyes.length,
    hair: RIG_TRAIT_OPTIONS.hair.length,
    hairColor: RIG_TRAIT_OPTIONS.hairColor.length,
    outfit: RIG_TRAIT_OPTIONS.outfit.length,
    accessory: RIG_TRAIT_OPTIONS.accessory.length,
    effect: RIG_TRAIT_OPTIONS.effect.length,
  };
  for (const key of Object.keys(counts) as Array<keyof TraitSelection>) {
    if (!Number.isInteger(traits[key]) || traits[key] < 0 || traits[key] >= counts[key]) {
      throw new RangeError(`Invalid ${key} trait ${traits[key]}`);
    }
  }
};

const styleBlock = (traits: TraitSelection) => {
  const [skin, skinShadow] = SKIN_PALETTES[traits.base];
  const [hair, hairShadow, hairHighlight] = HAIR_PALETTES[traits.hairColor];
  const [cloth, clothShadow, accent] = OUTFIT_PALETTES[traits.outfit];
  const eye = EYE_COLORS[traits.eyes];
  return `<style>.outline{stroke:#080b18;stroke-width:8;stroke-linejoin:round}.skin{fill:${skin}}.skinShadow{fill:${skinShadow}}.faceLine{stroke:${skinShadow}}.mouth{stroke:#a94962}.mouthSoft{stroke:#df8294}.hair{fill:${hair}}.hairShadow{fill:${hairShadow}}.hairHighlight{stroke:${hairHighlight};opacity:.55}.eyeWhite{fill:#fff}.eyeColor{fill:${eye}}.eyeLine,.brow{stroke:#111525;stroke-linejoin:round}.pupil{fill:#0a0d18}.shine{fill:#fff}.cloth{fill:${cloth}}.clothShadow{fill:${clothShadow}}.accent{fill:${accent}}.detailLine{stroke:${accent};stroke-width:8;fill:none}.darkAccessory{fill:#202540}.accessory{fill:#2d3358}.accessoryLine{stroke:#0a0c18;stroke-width:9;stroke-linejoin:round}.accentLine{stroke:#52eadb;stroke-width:9;fill:none;stroke-linecap:round}.gold{fill:#f3c95d}.goldLine{stroke:#f3c95d;stroke-width:9;fill:none;stroke-linecap:round}.violet{fill:#a06be8}.glass{fill:#70f0e4;fill-opacity:.45}.shineLine{stroke:#fff;stroke-opacity:.75;stroke-width:7;fill:none;stroke-linecap:round}</style>`;
};

const effect = (value: number) => {
  return EFFECTS[value];
};

const accessoryClip = (value: number) => {
  if ([3, 4, 8, 11].includes(value)) return "accessoryHeadTop";
  if ([1, 7].includes(value)) return "accessoryEyes";
  if (value === 5) return "accessoryEars";
  if (value === 6) return "accessoryLowerFace";
  if (value === 9) return "accessoryOutfit";
  if ([2, 10].includes(value)) return "accessoryComms";
  return "canvas";
};

export function renderAiAvatar(traits: TraitSelection, backgroundId: number, rarity: Rarity, label: string) {
  assertTraits(traits);
  if (!Number.isInteger(backgroundId) || backgroundId < 0 || backgroundId >= BACKGROUND_DEFS.length) {
    throw new RangeError(`Invalid background ${backgroundId}`);
  }
  const defs = `${BACKGROUND_DEFS[backgroundId]}${styleBlock(traits)}<clipPath id="outfitZone"><rect x="45" y="518" width="630" height="202"/></clipPath><clipPath id="eyeZone"><rect x="205" y="240" width="310" height="155"/></clipPath><clipPath id="accessoryHeadTop"><rect x="145" y="25" width="430" height="230"/></clipPath><clipPath id="accessoryEyes"><rect x="205" y="250" width="310" height="145"/></clipPath><clipPath id="accessoryEars"><path d="M145 275h115v180H145zm315 0h115v180H460z"/></clipPath><clipPath id="accessoryLowerFace"><rect x="245" y="385" width="230" height="130"/></clipPath><clipPath id="accessoryOutfit"><rect x="200" y="520" width="320" height="200"/></clipPath><clipPath id="accessoryComms"><path d="M140 25h440v230H140zm0 230h120v215H140zm320 0h120v250H380v-70h80z"/></clipPath><clipPath id="canvas"><rect width="720" height="720" rx="42"/></clipPath>`;
  const clip = accessoryClip(traits.accessory);
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 720 720" role="img" aria-label="${label}" data-rig="${RIG_VERSION}" data-catalog="${RIG_CATALOG_HASH}"><defs>${defs}</defs><g clip-path="url(#canvas)"><rect width="720" height="720" fill="url(#bg)"/><rect width="720" height="720" fill="url(#grid)"/><ellipse cx="360" cy="340" rx="300" ry="300" fill="url(#halo)"/>${BACKGROUND_ART[backgroundId]}${effect(traits.effect)}<g clip-path="url(#${clip})">${ACCESSORY_BACK[traits.accessory]}</g>${HAIR_BACK[traits.hair]}<g clip-path="url(#outfitZone)">${OUTFIT_BACK[traits.outfit]}</g>${FACE[0]}<g clip-path="url(#outfitZone)">${OUTFIT_FRONT[traits.outfit]}</g><g clip-path="url(#eyeZone)">${EYES[traits.eyes]}</g>${HAIR_FRONT[traits.hair]}<g clip-path="url(#${clip})">${ACCESSORY_FRONT[traits.accessory]}</g><rect x="24" y="24" width="224" height="42" rx="21" fill="#060914" opacity=".82"/><text x="44" y="51" fill="#dcfff9" font-family="monospace" font-size="15" letter-spacing="1.7">ANISOUL // ${rarity.toUpperCase()}</text></g></svg>`;
}
